import angular from 'angular'

import { isPromise } from './bits'
import { get, wrap, assign, isFunction } from 'lodash-es'

angular
  .module('ggs.utils')
  .factory('StayInQueue', StayInQueueProvider)

function StayInQueueProvider($log, $q, qh) {
  return StayInQueue

  function StayInQueue(resourceTag) {
    const sign = `stayInQueue[resource=${resourceTag}]`

    let isIdle = true
    const queue = []

    const BUSY = { isBusy: true }
    const FULL = { isFull: true }

    // ifIdle
    function ifIdle(action, onBusy, $args) {
      if (!isFunction(onBusy)) {
        onBusy = makeOnBusy(action)
      }

      return isIdle
        ? toQueue(action, $args)
        : qh.try(onBusy, [assign(new Error(`${sign}#ifIdle: resource is busy!`), BUSY)])
    }

    function makeIfIdle(action, onBusy) {
      if (!isFunction(onBusy)) {
        onBusy = makeOnBusy(action)
      }

      return queue_makeIfIdle_wrapper

      function queue_makeIfIdle_wrapper() {
        return ifIdle(action, onBusy, arguments)
      }
    }

    function makeOnBusy(action) {
      const name = get(action, 'name', 'unnamed')
      const message = `${name} action is skipped because ${sign} queue is busy!`
      return wrap(message, $log.debug)
    }

    // cancellable
    function cancellable(action, onCancel, $args) {
      let isCanceled = false
      const toReturn = toQueue(() => {
        if (!isCanceled) {
          return qh.try(action, $args)
        } else {
          return qh.try(onCancel, [qh.markCanceled(new Error(`${sign}#cancellable: action canceled!`))])
        }
      })

      toReturn.cancel = () => isCanceled = true
      return toReturn
    }

    function makeCancellable(action, onCancel) {
      return queue_makeCancellable_wrapper

      function queue_makeCancellable_wrapper() {
        return cancellable(action, onCancel, arguments)
      }
    }

    // onlyIfQueueLessThan
    function onlyIfQueueLessThan(threshold, action, onFull, $args) {
      const offset = Number(!isIdle)
      const realSize = offset + size()

      if (!isFunction(onFull)) {
        onFull = makeOnFull(threshold, action)
      }

      return realSize < threshold
        ? toQueue(action, $args)
        : qh.try(onFull, [assign(new Error(`${sign}#onlyIfQueueLessThan: resource queue is full!`), FULL)])
    }

    function makeOnlyIfQueueLessThan(threshold, action, onFull) {
      if (!isFunction(onFull)) {
        onFull = makeOnFull(threshold, action)
      }

      return queue_makeOnlyIfQueueLessThan_wrapper

      function queue_makeOnlyIfQueueLessThan_wrapper() {
        return onlyIfQueueLessThan(threshold, action, onFull, arguments)
      }
    }

    function makeOnFull(threshold, action) {
      const name = get(action, 'name', 'unnamed')
      const message = `${name} action is skipped because ${sign} queue has more than ${threshold} items!`
      return wrap(message, $log.debug)
    }

    // toQueue
    function toQueue(action, $args) {
      const deferred = $q.defer()
      if (isIdle) {
        runAction(action, deferred, $args)
      } else {
        queue.push({ action, deferred, $args })
      }

      return deferred.promise
    }

    function makeToQueue(action) {
      return queue_makeToQueue_wrapper

      function queue_makeToQueue_wrapper() {
        return toQueue(action, arguments)
      }
    }

    function size() {
      return queue.length
    }

    function idle() {
      return isIdle
    }

    function clear() {
      queue.length = 0
    }

    toQueue.ifIdle = ifIdle
    toQueue.cancellable = cancellable
    toQueue.onlyIfQueueLessThan = onlyIfQueueLessThan

    toQueue.make = makeToQueue
    toQueue.makeIfIdle = makeIfIdle
    toQueue.makeCancellable = makeCancellable
    toQueue.makeOnlyIfQueueLessThan = makeOnlyIfQueueLessThan

    toQueue.size = size
    toQueue.idle = idle
    toQueue.clear = clear

    return toQueue

    // helpers
    function tryRunNextAction() {
      if (isIdle && queue.length !== 0) {
        const { action, deferred, $args } = queue.shift()
        runAction(action, deferred, $args)
      }
    }

    function runAction(action, deferred, $args) {
      isIdle = false

      try {
        const res = action.apply(null, $args)
        if (isPromise(res)) {
          res
            .finally(onActionEnd)
            .then(deferred.resolve, deferred.reject)
        } else {
          onActionEnd()
          deferred.resolve(res)
        }
      } catch (err) {
        $log.warn(`${sign}#runAction: action throw error:`, err)
        onActionEnd()
        deferred.reject(err)
      }
    }

    function onActionEnd() {
      isIdle = true
      tryRunNextAction()
    }
  }
}
